import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress
} from '@mui/material';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { insertLocationCampaign, resetLocationCampaignState, verifyLocationCampaign } from '../../features/locationCampaign/locationCampaignSlice';
import { useSearchParams } from 'react-router-dom';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import Error from './Error';
import useActivity from "../../hooks/useActivity";

const maxFileSize = 10 * 1024 * 1024; // 10MB
const campaignUuid='KOZEL_2025_MEREU_IMPREUNA_CASTIGAM_PREMII';
const activityUuidOpenPage='QR_CODE_LOCATION_SCANNED';
export default function TombolaForm() {
  const dispatch = useAppDispatch();
  const { registerActivity } = useActivity();
  const [searchParams] = useSearchParams();
  const { loading, success, verifyLoading, currentCampaign, error } = useAppSelector(state => state.locationCampaign);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    dataNasterii: null as Dayjs | null,
    nume: '',
    prenume: '',
    telefon: '',
    numarBonFiscal: '',
    dataBonFiscal: null as Dayjs | null,
    incarcaPozaBon: null as File | null
  });
  
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  React.useEffect(() => {
  const locationCode = searchParams.get('code')|| '000000';

  // const campaignUuid = searchParams.get('campaign');

  dispatch(verifyLocationCampaign({
    location_code: locationCode,
    campaign_uuid: campaignUuid || undefined
  }));

  registerActivity({
    name: activityUuidOpenPage,
    payload: {
      campaign_slug: campaignUuid,
      details: {
        code: locationCode,
      }
    },
    callback: () => {},
  })

  return () => {
    dispatch(resetLocationCampaignState());
  };
}, [dispatch, searchParams]);

  // Funcție pentru convertirea fișierului în base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Validări
  const validateBirthdate = (date: Dayjs | null) => {
    if (!date) {
      setErrors(prevErrors => ({
        ...prevErrors,
        dataNasterii: 'Data nașterii este obligatorie',
      }));
      return false;
    }
    
    const today = dayjs();
    const age = today.diff(date, 'year');
    if (age < 18) {
      setErrors(prevErrors => ({
        ...prevErrors,
        dataNasterii: 'Trebuie să aveți cel puțin 18 ani.',
      }));
      return false;
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        dataNasterii: '',
      }));
      return true;
    }
  };

  const validatePhone = (phoneNr: string) => {
  if (!phoneNr) {
    setErrors(prevErrors => ({ 
      ...prevErrors, 
      telefon: 'Numărul de telefon este obligatoriu' 
    }));
    return false;
  }
  
  // Verifică dacă începe cu 07 și are exact 10 cifre
  if (!/^07\d{8}$/.test(phoneNr)) {
    setErrors(prevErrors => ({ 
      ...prevErrors, 
      telefon: 'Numărul de telefon trebuie să înceapă cu 07 și să aibă 10 cifre' 
    }));
    return false;
  } else {
    setErrors(prevErrors => ({ 
      ...prevErrors, 
      telefon: '' 
    }));
    return true;
  }
};

  // Funcție pentru resetarea datelor
  const resetFormData = () => {
    setFormData({
      dataNasterii: null,
      nume: '',
      prenume: '',
      telefon: '',
      numarBonFiscal: '',
      dataBonFiscal: null,
      incarcaPozaBon: null
    });
    setAgreedToTerms(false);
    setErrors({});
    dispatch(resetLocationCampaignState());
  };

  const handleInputChange = (field: string, value: string) => {
  // Validări specifice pentru unele câmpuri
  if (field === 'telefon' && !/^\d{0,10}$/.test(value)) {
    return; // Nu actualizează dacă nu sunt doar cifre și max 10
  }
  if (field === 'numarBonFiscal' && !/^\d*$/.test(value)) {
    return; // Nu actualizează dacă nu sunt doar cifre
  }
  
  setFormData(prev => ({
    ...prev,
    [field]: value
  }));

  // Validează în timp real pentru telefon
  if (field === 'telefon') {
    // Validează doar dacă utilizatorul a introdus ceva
    if (value.length > 0) {
      validatePhone(value);
    } else {
      // Dacă câmpul este gol, setează eroarea pentru câmp obligatoriu
      setErrors(prev => ({
        ...prev,
        telefon: 'Numărul de telefon este obligatoriu'
      }));
    }
  } else {
    // Pentru celelalte câmpuri (nume, prenume, etc.)
    if (value.trim()) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    } else {
      // Adaugă erori pentru câmpurile goale
      let errorMessage = '';
      if (field === 'nume') errorMessage = 'Numele este obligatoriu';
      if (field === 'prenume') errorMessage = 'Prenumele este obligatoriu';
      if (field === 'numarBonFiscal') errorMessage = 'Numărul bonului fiscal este obligatoriu';
      
      setErrors(prev => ({
        ...prev,
        [field]: errorMessage
      }));
    }
  }
};

  const handleDateChange = (field: string, date: Dayjs | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));

    // Validează în timp real pentru data nașterii
    if (field === 'dataNasterii') {
      validateBirthdate(date);
    } else if (field === 'dataBonFiscal') {
      // Validare pentru data bonului fiscal
      if (date) {
        setErrors(prev => ({
          ...prev,
          dataBonFiscal: ''
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          dataBonFiscal: 'Data bonului fiscal este obligatorie'
        }));
      }
    }
  };

  const handleFileChange = (file: File | null) => {
    // Verifică mărimea fișierului înainte de a-l seta
    if (file) {
      const maxSizeInBytes = 30 * 1024 * 1024; // 30MB 

      if (file.size > maxSizeInBytes) {
        // Setează eroarea și nu accepta fișierul
        setErrors(prev => ({
          ...prev,
          incarcaPozaBon: 'Fișierul este prea mare. Mărimea maximă permisă este 30MB.'
        }));
        // Nu seta fișierul în formData
        setFormData(prev => ({
          ...prev,
          incarcaPozaBon: null
        }));
        return;
      }
    }

    // Dacă fișierul este valid sau null, setează-l
    setFormData(prev => ({
      ...prev,
      incarcaPozaBon: file
    }));

    // Șterge/adaugă erori pentru fișier
    if (file) {
      setErrors(prev => ({
        ...prev,
        incarcaPozaBon: ''
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        incarcaPozaBon: 'Bonul fiscal trebuie încărcat'
      }));
    }
  };

  const handleTermsChange = (agreed: boolean) => {
    setAgreedToTerms(agreed);
    
    // Șterge/adaugă erori pentru terms
    if (agreed) {
      setErrors(prev => ({
        ...prev,
        agreedToTerms: ''
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        agreedToTerms: 'Trebuie să acceptați regulamentul'
      }));
    }
  };

  const handleSubmit = async () => {
  // Validare înaintea submit-ului
if (!formData.dataNasterii || !formData.nume || !formData.prenume || !formData.telefon || 
      !formData.numarBonFiscal || !formData.dataBonFiscal || !formData.incarcaPozaBon ||
      errors.dataNasterii || errors.nume || errors.prenume || errors.telefon || 
      errors.numarBonFiscal || errors.dataBonFiscal || errors.incarcaPozaBon) {
    return;
  }

  try {
    // Creează FormData pentru trimiterea ca multipart/form-data
    const formDataPayload = new FormData();
    
    // Adaugă câmpurile conform validării din LocationCampaignController
    formDataPayload.append('birthdate', formData.dataNasterii!.format('YYYY-MM-DD'));
    formDataPayload.append('firstname', formData.prenume);
    formDataPayload.append('lastname', formData.nume);
    formDataPayload.append('phone', formData.telefon);
    formDataPayload.append('location_code', searchParams.get('code') || '000000');
    formDataPayload.append('campaign_uuid', currentCampaign?.campaign_uuid || campaignUuid );
    formDataPayload.append('img_bon_fiscal', formData.incarcaPozaBon!);
    formDataPayload.append('bon', formData.numarBonFiscal);
    formDataPayload.append('data_bon', formData.dataBonFiscal!.format('YYYY-MM-DD'));

    const result = await dispatch(insertLocationCampaign(formDataPayload));
    
    if (insertLocationCampaign.fulfilled.match(result)) {
      setCurrentStep(4);
    }
  } catch (error) {
    // Error will be handled by the slice
  }
};

if (verifyLoading) {
  return (
    <Container maxWidth="sm">
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography ml={2}>Se verifică campania...</Typography>
      </Box>
    </Container>
  );
}

// Error state - campanie inexistentă
if (error && !currentCampaign) {
  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          Campanie indisponibilă
        </Typography>
        <Typography paragraph>{error}</Typography>
        <Typography>Te rugăm să verifici codul QR și să încerci din nou.</Typography>
      </Paper>
    </Container>
  );
}

// Dacă nu avem campanie validă
if (!currentCampaign) {
  return (
    <Container sx={{ p: 0, w: 1, mt: 2, mb: 2 }}>
      <Paper elevation={3} sx={{ p: 2, pt: 15, pb: 15, textAlign: 'center' }}>
        <Typography color={'white'} fontSize={'2rem'}>Nu s-a găsit o campanie activă pentru această locație.<br/> Scanează un cod QR din locațiile participante. Mulțumim!</Typography>
      </Paper>
    </Container>
  );
}

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepOne
            dataNasterii={formData.dataNasterii}
            onDateChange={(date) => handleDateChange('dataNasterii', date)}
            onNext={() => setCurrentStep(2)}
            errors={errors}
          />
        );
      case 2:
        return (
          <StepTwo
            nume={formData.nume}
            prenume={formData.prenume}
            telefon={formData.telefon}
            onInputChange={handleInputChange}
            onNext={() => setCurrentStep(3)}
            errors={errors}
          />
        );
      case 3:
        return (
          <StepThree
            dataBonFiscal={formData.dataBonFiscal}
            numarBonFiscal={formData.numarBonFiscal}
            incarcaPozaBon={formData.incarcaPozaBon}
            onDateChange={(date) => handleDateChange('dataBonFiscal', date)}
            onInputChange={handleInputChange}
            onFileChange={handleFileChange}
            onSubmit={handleSubmit}
            errors={errors}
          />
        );
      case 4:
        return (
          <StepFour
            onNext={() => {
              // Resetează toate datele și navighează înapoi la StepOne
              resetFormData();
              setCurrentStep(1);
            }}
          />
        );
       case 5:
        return (
          <Error
            onNext={() => {
              // Resetează toate datele și navighează înapoi la StepOne
              resetFormData();
              setCurrentStep(1);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {renderStep()}
    </Box>
  );
}